import { useEffect } from 'react';
import { isTechnologyRequired } from 'domain/shared/career';
import { SkillsInput, useSkillsService } from 'domain/recruiting/shared/skills';
import { CANDIDATE_TAGS } from 'domain/shared/newVacancy';
import {
  CheckboxGroup,
  CountryDropdown,
  DateInput,
  Dropdown,
  type FormFieldsData,
  useFormContext,
} from 'framework/form';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import type { SourcingFilterOptions } from '../../../../types';
import { SOURCING_FILTERS, SUPPORTED_COMPETENCY_LEVELS } from '../../constants';
import StrictFilterToggle from '../StrictFilterToggle';
import EnglishLevelsSourcingFilter from '../EnglishLevelsSourcingFilter';
import { findSourcingSpecialization } from '../../utils/sourcingFilterModel';

type Props = {
  data: FormFieldsData;
  filterOptions: SourcingFilterOptions;
};

const FULL_WIDTH = { xs: 12, md: 12, xl: 12 };
const THIRD_WIDTH = { xs: 12, md: 4, xl: 4 };
const TRHREE_QUARTERS_WIDTH = { xs: 12, md: 8, xl: 8 };

const tagsKeys = Object.keys(CANDIDATE_TAGS);

const SourcingSkillsInput = () => {
  const { searchByName } = useSkillsService();
  return <SkillsInput source={SOURCING_FILTERS.skills} label="" loadSuggestionsMethod={searchByName} />;
};

const SourcingTechnologyDropdown = ({ filterOptions }: { filterOptions: SourcingFilterOptions }) => {
  const { data, onChangeCallback } = useFormContext();
  const specialization = findSourcingSpecialization(filterOptions, data?.specializationId);
  const currentTechnologyId =
    typeof data?.technologyId === 'string' || typeof data?.technologyId === 'number'
      ? String(data.technologyId)
      : undefined;
  const currentTechnology = specialization?.technologies.find(
    technology => technology.technologyId === currentTechnologyId,
  );
  const onlyTechnology = specialization?.technologies.length === 1 ? specialization.technologies[0] : undefined;
  const nextTechnologyId = currentTechnology?.technologyId ?? onlyTechnology?.technologyId ?? null;

  useEffect(() => {
    if (currentTechnologyId !== (nextTechnologyId ?? undefined)) {
      onChangeCallback?.({ technologyId: nextTechnologyId });
    }
  }, [currentTechnologyId, nextTechnologyId, onChangeCallback]);

  if (!specialization) return null;

  const technologyRequired = isTechnologyRequired(specialization.specializationId);
  if (!technologyRequired && specialization.technologies.length === 0) return null;

  return (
    <Dropdown
      source={SOURCING_FILTERS.technology}
      label="Technology"
      placeholder="Technology"
      isClearable={!technologyRequired}
      isRequired={technologyRequired}
      disabled={specialization.technologies.length <= 1}
      options={specialization.technologies.map(option => ({
        value: option.technologyId,
        label: option.technology,
      }))}
    />
  );
};

const getSettingsSourcingDrawerSections = ({ filterOptions }: Props): CreateEditFormSection[] => {
  return [
    {
      id: 'specialization',
      title: 'Specialization',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.specialization} disabled />,
      fields: [
        {
          id: 'specializationId',
          size: THIRD_WIDTH,
          content: (
            <Dropdown
              source={SOURCING_FILTERS.specialization}
              label="Specialization"
              placeholder="Specialization"
              isClearable={false}
              options={filterOptions.specializations.map(option => ({
                value: option.specializationId,
                label: option.specialization,
              }))}
            />
          ),
        },
        {
          id: 'technologyId',
          size: THIRD_WIDTH,
          content: <SourcingTechnologyDropdown filterOptions={filterOptions} />,
        },
      ],
    },
    {
      id: 'levels',
      title: 'Level',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.levels} />,
      fields: [
        {
          id: 'levels',
          size: FULL_WIDTH,
          content: (
            <CheckboxGroup
              options={SUPPORTED_COMPETENCY_LEVELS.map(level => ({
                value: level,
                label: level,
              }))}
              label=""
              source={SOURCING_FILTERS.levels}
              sx={{
                '& .MuiFormGroup-root': {
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gridTemplateRows: 'repeat(4, auto)',
                  gridAutoFlow: 'column',
                },
                '& .checkbox-form-group .MuiButtonBase-root': {
                  px: 1,
                  py: 0,
                },
              }}
            />
          ),
        },
      ],
    },
    {
      id: 'location',
      title: 'Location',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.location} />,
      fields: [
        {
          id: 'location',
          size: TRHREE_QUARTERS_WIDTH,
          content: <CountryDropdown multiple source={SOURCING_FILTERS.location} label="Country" />,
        },
      ],
    },
    {
      id: 'english',
      title: 'Min English level',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.englishLevel} />,
      fields: [
        {
          id: 'english',
          size: FULL_WIDTH,
          content: <EnglishLevelsSourcingFilter />,
        },
      ],
    },
    {
      id: 'skills',
      title: 'Skills',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.skills} />,
      fields: [
        {
          id: 'skills',
          size: TRHREE_QUARTERS_WIDTH,
          content: <SourcingSkillsInput />,
        },
      ],
    },
    {
      id: 'tags',
      title: 'Tags',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.tags} />,
      content: (
        <CheckboxGroup
          options={tagsKeys.map(tag => ({
            value: tag,
            label: CANDIDATE_TAGS[tag as keyof typeof CANDIDATE_TAGS],
          }))}
          label=""
          source={SOURCING_FILTERS.tags}
          sx={{
            '& .checkbox-form-group .MuiButtonBase-root': {
              px: 1,
              py: 0,
            },
          }}
        />
      ),
    },
    {
      id: 'interaction',
      title: 'Last interaction',
      actions: <StrictFilterToggle filterKey={SOURCING_FILTERS.lastInteraction} />,
      fields: [
        {
          id: 'value.from',
          content: <DateInput source={`${SOURCING_FILTERS.lastInteraction}.from`} label="From" />,
        },
        {
          id: 'value.to',
          content: <DateInput source={`${SOURCING_FILTERS.lastInteraction}.to`} label="Until" />,
        },
      ],
    },
  ];
};

export default getSettingsSourcingDrawerSections;
